import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import UserContext from "../../common/UserContext";
import {GeneralContext, SearchProductContext} from "../FSARequestLicenseContext";
import {useLocalization} from "@progress/kendo-react-intl";
import {Grid, GridColumn, GridNoRecords} from "@progress/kendo-react-grid";
import {
    descriptionKey, emptySearchKey, endDateKey,
    licenseTypeKey,
    mainMessages, NoRecordsSearchKey,
    productNumberKey,
    qtyKey, startDateKey, totalKey
} from "../../../assets/text/MultilingualText";
import {ColumnMenu, NoWrapCell} from "../../common/Grid";
import {SearchedQtyCell} from "./SearchedQtyCell";
import {LicenseTypeCell} from "./LicenseTypeCell";
import {CartCell} from "./CartCell";
import {process} from "@progress/kendo-data-query";
import {SearchedDateHeaderCell, EndDatePickerCell, StartDatePickerCell} from "./DatePickerCell";
import {Popover} from "@progress/kendo-react-tooltip";
import {Calendar} from "@progress/kendo-react-dateinputs";

export const DateGridContext = createContext({});
export const SearchedProductsGrid = props => {
    const {
        displaySearchMessage,
    } = props;

    const {
        siteLanguage,
        siteLanguageDefault,
    } = useContext(UserContext);

    const {
        initialGridState,
        uniqueID,
        licenseStartDateKey,
        licenseEndDateKey,
        headerInfo,
        minDate,
        minDurationDate,
        maxDate,
        validateDates,
        setShowSecondGenericError,
    } = useContext(GeneralContext);

    const {
        searchedProducts: items,
        setSearchedProducts: setItems,
        searchedGridState: gridState,
        setSearchedGridState: setGridState,
        searchedDataState: dataState,
        setSearchedDataState: setDataState
    } = useContext(SearchProductContext);

    const localization = useLocalization();

    const gridInitMessage = localization.toLanguageString(NoRecordsSearchKey, mainMessages[siteLanguageDefault][NoRecordsSearchKey])
    const gridNotInitMessage = localization.toLanguageString(emptySearchKey, mainMessages[siteLanguageDefault][emptySearchKey])
    const [gridNoRecordsMessage, setGridNoRecordsMessage] = useState(gridInitMessage)

    useEffect(() => {
        if (displaySearchMessage) {
            setGridNoRecordsMessage(gridInitMessage)
        } else {
            setGridNoRecordsMessage(gridNotInitMessage)
        }
    }, [siteLanguage, displaySearchMessage, gridInitMessage, gridNotInitMessage])

    const onDataStateChange = useCallback((event) => {
        const newDataState = process(
            items
                .map((item) => ({
                    ...item,
                })),
            event.dataState
        );
        setGridState(event.dataState);
        setDataState(newDataState);
    }, [items]);

    useEffect(() => {
        const newDataState = process(
            items
                .map((item) => ({
                    ...item,
                })),
            gridState
        );
        setDataState(newDataState);
    }, [items]) // eslint-disable-line react-hooks/exhaustive-deps

    const onItemChange = (e) => {
        setShowSecondGenericError(false)
        let newData = items.map((item) => {
            if (item[uniqueID] === e.dataItem[uniqueID]) {

                //reset cart error if input is changed
                item['exceedError'] = false

                if (e.field === 'license_type') {
                    let selectExist = false
                    item.license_type.forEach(type => {
                        type.selected = false
                        if (type.text === 'Select') {
                            selectExist = true
                        }
                        if (type.text === e.value.text) {
                            type.selected = true
                        }
                    })

                    //remove the default select option
                    if (selectExist) {
                        item.license_type.shift()
                    }
                } else {
                    item[e.field || ''] = e.value;
                    if (e.field === licenseStartDateKey || e.field === licenseEndDateKey) {
                        item = validateDates(item, minDate, maxDate, headerInfo.minimum_duration)
                    }
                }
            }
            return item;
        });
        setItems([...newData]);
    };

    //calender date selector popover
    const [anchor, setAnchor] = useState(null);
    const [visible, setVisible] = useState(false);
    const [fieldName, setFieldName] = useState("");

    const changeAllDates = (field, value) => {
        setShowSecondGenericError(false);
        let newData = items.map(item => {
            //reset cart error if input is changed
            item['exceedError'] = false

            item[field] = value
            return validateDates(item, minDate, maxDate, headerInfo.minimum_duration)
        })
        setItems(newData)
    }

    const onMouseOut = (ev) => {
        let isChildEl = ev.currentTarget.contains(ev.relatedTarget);
        if (!isChildEl) setVisible(false);
    };


    return (
        <>
            <Popover
                className="ksm-header-popover"
                show={visible}
                anchor={anchor}
                position={'bottom'}
                style={{padding: "none"}}
            >
                <div onMouseOut={onMouseOut}>
                    {fieldName === licenseStartDateKey && (
                        <Calendar
                            onChange={(e) => {
                                changeAllDates(fieldName, e.value)
                                setVisible(false);
                            }}
                            min={minDate}
                            max={maxDate}
                        />
                    )}

                    {fieldName === licenseEndDateKey && (
                        <Calendar
                            onChange={(e) => {
                                changeAllDates(fieldName, e.value)
                                setVisible(false);
                            }}
                            min={minDurationDate}
                            max={maxDate}
                        />
                    )}
                </div>

            </Popover>
            <DateGridContext.Provider
                value={{
                    visible,
                    setVisible,
                    anchor,
                    setAnchor,
                    setFieldName,
                    items,
                }}>
                <Grid
                    className={'fsa-searched-product-grid'}
                    scrollable={'none'}
                    data={dataState}
                    sortable={true}
                    pageable={(items.length <= initialGridState.take) ? false : {
                        buttonCount: 5,
                        pageSizes: [10, 20, 50, 100],
                    }}
                    onDataStateChange={onDataStateChange}
                    total={dataState.total}
                    editField="inEdit"
                    onItemChange={onItemChange}
                    {...gridState}
                >
                    <GridNoRecords>
                        {gridNoRecordsMessage}
                    </GridNoRecords>
                    <GridColumn
                        field="product_number"
                        title={localization.toLanguageString(productNumberKey, mainMessages[siteLanguageDefault][productNumberKey])}
                        columnMenu={ColumnMenu}
                        cell={NoWrapCell}
                    />
                    <GridColumn
                        field="description"
                        title={localization.toLanguageString(descriptionKey, mainMessages[siteLanguageDefault][descriptionKey])}
                        columnMenu={ColumnMenu}
                    />
                    <GridColumn
                        field="quantity"
                        title={localization.toLanguageString(qtyKey, mainMessages[siteLanguageDefault][qtyKey])}
                        cell={SearchedQtyCell}
                        sortable={false}
                    />
                    <GridColumn
                        field="license_type"
                        title={localization.toLanguageString(licenseTypeKey, mainMessages[siteLanguageDefault][licenseTypeKey])}
                        cell={LicenseTypeCell}
                        sortable={false}
                    />
                    <GridColumn
                        field={licenseStartDateKey}
                        title={localization.toLanguageString(startDateKey, mainMessages[siteLanguageDefault][startDateKey])}
                        // headerCell={SearchedDateHeaderCell}
                        cell={StartDatePickerCell}
                        sortable={false}
                    />
                    <GridColumn
                        field={licenseEndDateKey}
                        title={localization.toLanguageString(endDateKey, mainMessages[siteLanguageDefault][endDateKey])}
                        headerCell={SearchedDateHeaderCell}
                        cell={EndDatePickerCell}
                        sortable={false}
                    />
                    <GridColumn
                        field={null}
                        title={" "}
                        cell={CartCell}
                        sortable={false}
                    />
                </Grid>
            </DateGridContext.Provider>
        </>
    )
}